import React, { useEffect, useRef, useState } from 'react';
import { Box, EmptyContent, Flex } from '@/components/ui';
import { PreviewRendererProps } from '../types';
import { IconAlertCircle, IconPhoto } from '@tabler/icons-react';
import { api } from '@/services/http';

const ImagePreviewRenderer: React.FC<PreviewRendererProps> = ({
  apiEndpoint,
  filename,
}) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [imageBlobUrl, setImageBlobUrl] = useState<string | null>(null);
  const createdBlobUrlRef = useRef<string | null>(null);

  // Load image from apiEndpoint as blob
  useEffect(() => {
    let isCancelled = false;

    const loadImage = async () => {
      // Reset states
      setImageError(false);
      setImageLoading(true);

      // Clean up previous blob URL if we created it
      if (createdBlobUrlRef.current) {
        URL.revokeObjectURL(createdBlobUrlRef.current);
        createdBlobUrlRef.current = null;
      }

      // Load from apiEndpoint
      if (!apiEndpoint) {
        setImageError(true);
        setImageLoading(false);
        return;
      }

      try {
        const response = await api.get(apiEndpoint, {
          responseType: 'blob',
          headers: {
            Accept: 'image/*',
          },
        });

        if (isCancelled) return;

        // Verify that we got a blob
        if (!(response.data instanceof Blob)) {
          setImageError(true);
          setImageLoading(false);
          return;
        }

        const blobUrl = URL.createObjectURL(response.data);
        createdBlobUrlRef.current = blobUrl;
        setImageBlobUrl(blobUrl);
        setImageLoading(false);
      } catch {
        if (!isCancelled) {
          setImageError(true);
          setImageLoading(false);
        }
      }
    };

    loadImage();

    return () => {
      isCancelled = true;
      // Clean up blob URL when component unmounts or dependencies change
      if (createdBlobUrlRef.current) {
        URL.revokeObjectURL(createdBlobUrlRef.current);
        createdBlobUrlRef.current = null;
      }
    };
  }, [apiEndpoint]);

  const handleImageLoad = () => {
    setImageLoading(false);
  };

  const handleImageError = () => {
    setImageLoading(false);
    setImageError(true);
  };

  if (imageError) {
    return (
      <EmptyContent
        size="medium"
        state="error"
        icon={<IconAlertCircle />}
        title="Image Preview Not Available"
        description="Unable to load the image. The file may be corrupted or in an unsupported format."
      />
    );
  }

  // Show loading state while loading or if blob URL is not ready
  if (imageLoading || !imageBlobUrl) {
    return (
      <EmptyContent
        size="medium"
        icon={<IconPhoto />}
        title="Loading Image"
        description="Please wait while the image loads..."
      />
    );
  }

  return (
    <Flex direction="column" align="center" gap="3">
      <Box
        style={{
          maxWidth: '100%',
          maxHeight: '70vh',
          overflow: 'auto',
        }}
      >
        <img
          src={imageBlobUrl}
          alt={filename}
          onLoad={handleImageLoad}
          onError={handleImageError}
          style={{
            maxWidth: '100%',
            height: 'auto',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          }}
        />
      </Box>
    </Flex>
  );
};

export default ImagePreviewRenderer;
